/**
 * Modelo de tempo (§10.1.1, FE §3.14): `t_min` = minutos desde 00h00 de Brasília (UTC−3 fixo, sem horário de verão)
 * do dia da eleição, com fração; `t_min ≥ 1440` é segunda, 26/10; `t_min < 0` é antes do dia. Só constantes e funções
 * puras: o sinal `now` e o tique único de 1 s (`useNow`, §10.1.3) ficam em `state/` (DE-12 = DE-B01), que usa
 * `clockAt` e `nextTickDelay` daqui. Os formatadores de hora são de src/format/time.ts (§13.4.3).
 *
 * O `epoch` padrão é o da data padrão do processo (`currentEpochMs()`): 00h00 de Brasília de 25/10/2026
 * (`ELECTION_DATE`) no site ao vivo, e o da noite aberta na página do replay (`configureElectionDate`, de
 * src/format/time.ts). Quem lê `config.election_date` passa `epochMs(config.election_date)`.
 */
import { currentEpochMs, ELECTION_DATE, epochMs } from '../format/time';

export { currentElectionDate, currentEpochMs } from '../format/time';
export { ELECTION_DATE, epochMs };

// ── Janela da noite (§10.1.1, §10.2) ────────────────────────────────────────────────────────────────────────
/** 17h00: abertura da apuração (liberação dos resultados) e início da trilha. */
export const T_OPEN = 1020;
/** 22h00: fim nominal da noite (fim padrão da trilha). */
export const T_NIGHT_END = 1320;
/** 00h00 de 26/10 (segunda-feira). */
export const T_DAY = 1440;
/** 06h00 de 26/10: teto absoluto da trilha. */
export const T_CAP = 1800;

/** 00h00 de Brasília de 25/10/2026 em ms (2026-10-25T03:00:00Z). */
export const ELECTION_EPOCH_MS = epochMs(ELECTION_DATE);

// ── Conversões ──────────────────────────────────────────────────────────────────────────────────────────────
/** ms → t_min. */
export const minuteOf = (ms: number, epoch: number = currentEpochMs()): number => (ms - epoch) / 60_000;
/** t_min → ms. */
export const msOf = (tMin: number, epoch: number = currentEpochMs()): number => epoch + tMin * 60_000;
/** ISO-8601 com offset → t_min ("2026-10-25T17:00:00-03:00" → 1020). */
export const tMinOfIso = (iso: string, epoch: number = currentEpochMs()): number => minuteOf(Date.parse(iso), epoch);

/** Abertura já passou (17h00:00 inclusive)? Base da trava E01 e da fase `pre_open` (§13.1.2). */
export const isOpen = (tMin: number): boolean => tMin >= T_OPEN;

// ── Relógio (§10.1.2, §10.1.3) ──────────────────────────────────────────────────────────────────────────────
/** Leitura do relógio da fonte: o valor do sinal `now` (`NowState` de state/store.ts). */
export interface ClockReading {
  ms: number;
  tMin: number;
}

/** `{ ms, tMin }` de um instante do relógio da fonte (`source.clock()`, DE-B03). */
export function clockAt(ms: number, epoch: number = currentEpochMs()): ClockReading {
  return { ms, tMin: minuteOf(ms, epoch) };
}

/** Atraso do próximo tique alinhado ao segundo (§10.1.3): `1005 − (ms % 1000)`, entre 6 e 1005 ms. */
export function nextTickDelay(ms: number): number {
  return 1005 - (((ms % 1000) + 1000) % 1000);
}

/**
 * Segundos inteiros que faltam até `targetTMin` (contagem regressiva até 17h00:00, §10.8), arredondados para cima:
 * a 0,5 s da abertura ainda falta 1; 0 a partir do instante-alvo.
 */
export function secondsUntil(targetTMin: number, ms: number, epoch: number = currentEpochMs()): number {
  return Math.max(0, Math.ceil((msOf(targetTMin, epoch) - ms) / 1000 - 1e-6));
}
